// Web-app-only "Item Template" shortcut. Enter an existing item number and click
// Load: the app fetches that item's custitem_mapping from NetSuite, decodes it,
// and pre-fills the whole configurator from it. A starting point, not a NetSuite
// write. `compact` renders it inline for the page header (always visible above
// the tabs); the default renders it as a form field in the question grid.

import { useEffect, useState } from "react";
import { useItemLoader } from "../state/useItemLoader";
import { useAnswers } from "../state/AnswersContext";

export default function ItemTemplateField({ compact = false }: { compact?: boolean }) {
  const loadItem = useItemLoader();
  const { forceNewConfig, editExistingItem, syncCustomerPartNumberForItem } = useAnswers();
  const [value, setValue] = useState("");
  const [status, setStatus] = useState<{ kind: "ok" | "err" | "info"; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  // Auto-dismiss the success message after a few seconds (errors stay so they
  // can be read; "Loading…" is transient and replaced by the result).
  useEffect(() => {
    if (status?.kind !== "ok") return;
    const t = window.setTimeout(() => setStatus(null), 4000);
    return () => window.clearTimeout(t);
  }, [status]);

  const load = async () => {
    const itemNumber = value.trim();
    if (!itemNumber || busy) return;
    setBusy(true);
    setStatus({ kind: "info", text: "Loading…" });
    try {
      // Prefer the full snapshot; falls back to the mapping when the saved
      // config is missing or unreadable (see useItemLoader).
      const res = await loadItem(itemNumber);
      // Point the Customer Part Number field at the item we just loaded: its real
      // CPN, or CLEARED when it has none. Whatever was in the field belonged to a
      // DIFFERENT part, and leaving it would claim that number away from that part
      // on save. Runs for both outcomes below (editable or locked) and whether the
      // input was an item number or a part number — so typing a part number in any
      // casing comes back as the real, correctly-cased one.
      if (res.kind === "ok") {
        await syncCustomerPartNumberForItem(res.itemInternalId ?? null);
      }
      // If the loaded item is editable (custitem_cannoteditcpq false), switch to
      // Edit mode on THAT item so Save & Submit UPDATES it in place — item,
      // description, name, and its BOM/routing — rather than spawning a new part.
      // A locked item (or one we couldn't resolve an internal id for) falls back
      // to New Config, the prior behavior.
      if (res.kind === "ok" && !res.cannotEdit && res.itemInternalId) {
        editExistingItem(res.itemInternalId);
        setStatus({ kind: "ok", text: `${res.text} · Editing this item in place — Save & Submit updates it (incl. BOM & routing).` });
      } else {
        forceNewConfig();
        setStatus(res);
      }
    } catch (e) {
      setStatus({ kind: "err", text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };

  const input = (
    <input
      type="text"
      className="cpq-input"
      value={value}
      placeholder="Item number or customer part #"
      onChange={(e) => setValue(e.target.value)}
      onKeyDown={(e) => { if (e.key === "Enter") load(); }}
    />
  );
  const button = (
    <button type="button" className="cpq-btn cpq-btn-primary" onClick={load} disabled={busy || !value.trim()}>
      {busy ? "Loading…" : "Load"}
    </button>
  );
  const statusEl = status && (
    <div className={`cpq-item-template-status is-${status.kind}`}>{status.text}</div>
  );

  if (compact) {
    // Header layout: label + input + button on one inline row; the status
    // floats just below so it never changes the header height.
    return (
      <div className="cpq-header-template" data-code="ITEM_TEMPLATE">
        <label className="cpq-header-template-label">Item Template</label>
        <div className="cpq-item-template-row">
          {input}
          {button}
        </div>
        {statusEl}
      </div>
    );
  }

  return (
    <div className="cpq-question" data-code="ITEM_TEMPLATE" style={{ gridColumn: "span 4" }}>
      <label className="cpq-question-label">Item Template</label>
      <div className="cpq-item-template-row">
        {input}
        {button}
      </div>
      {statusEl}
    </div>
  );
}
